import { FeaturesReady, useFeatureIsOn } from '@growthbook/growthbook-react';
import { Box, Button, Typography } from '@mui/material';
import classNames from 'classnames';
import SearchFilter, { SearchFiltersQueryParams } from 'components/FilterMenu/SearchFilter/SearchFilter';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import HeaderItem from 'components/Header/header-items/HeaderItem';
import HeaderLogin from 'components/Header/header-items/HeaderLogin/HeaderLogin';
import HeaderSearch from 'components/Header/header-items/HeaderSearch/HeaderSearch';
import NotFound from 'components/NotFound/NotFound';
import ProductsListInfiniteScroll from 'components/ProductsListInfiniteScroll/ProductsListInfiniteScroll';
import { BRAND, SEARCH } from 'config/fe-routes';
import useProductsList from 'hooks/useProductsList';
import isEqual from 'lodash.isequal';
import { useRouter } from 'next/router';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BrandService from 'services/brand.service';
import ProductService from 'services/product.service';
import { RecsysTracking } from 'services/recsysTracking.service';
import { searchItemClick } from 'store/actions/search.actions';
import { useAppDispatch } from 'store/hooks';
import { Brand } from 'types/brand.types';
import { FILTER_MENU_ITEM } from 'types/filter-menu.types';
import { GTM, GTMLocations, SEARCH_LIST_ID } from 'utils/analytics';
import { buildSearchTrackingFilters } from 'utils/buildSearchTrackingFilters';
import { toKebabCase } from 'utils/format-text';
import { generateSearchGuid } from 'utils/generateSearchGuid';
import ImageUrlResizer from 'utils/image-url-resizer';
import { extractQueryParams, redirectTo } from 'utils/route-helpers';
import { getJsonLdBreadcrumbsList, getSEOFriendlyTitle } from 'utils/seo-data';
import { formatServerSideProps } from 'utils/server-side-props';

import { SaveSearchButton } from '../../../components/SaveSearchButton/SaveSearchButton';
import { useLanguage } from '../../../hooks/useLanguage';
import { UserSavedSearchTargets } from '../../../types/userSavedSearch.types';
import { parseJSON } from '../../../utils/parse-json';
import { useIsScreenSizeMax } from '../../../utils/screen-size';
import captureException from '../../../utils/sentry';
import { stringifyJSON } from '../../../utils/stringifyJSON';

type Props = {
  brand?: Brand;
  queryParams?: any;
  jsonLdBreadcrumbs: any[];
};

const productService = new ProductService();
const brandService = new BrandService();

// cache keys - will be suffixed with language
const CACHE_KEY_BASE = 'brandSearchCacheKey';
const PRODUCTS_CACHE_KEY_BASE = 'brandSearchProducts';
const HAS_MORE_PRODUCTS_CACHE_KEY_BASE = 'brandSearchProductsHasMore';

// Results configuration
const PRODUCTS_PER_ROW = 6;
const PRODUCTS_PER_PAGE = PRODUCTS_PER_ROW * 10;

const BrandProducts = ({ brand, jsonLdBreadcrumbs }: Props) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const router = useRouter();
  const { query } = router;
  const isMobileScreen = useIsScreenSizeMax('md');
  const multilangSearchEnabled = useFeatureIsOn('multilang_search_enabled');

  // Add language to cache keys to maintain separate caches per language
  const CACHE_KEY = `${CACHE_KEY_BASE}_${language}`;
  const PRODUCTS_CACHE_KEY = `${PRODUCTS_CACHE_KEY_BASE}_${language}`;
  const HAS_MORE_PRODUCTS_CACHE_KEY = `${HAS_MORE_PRODUCTS_CACHE_KEY_BASE}_${language}`;

  const { setHasMoreProducts, resetAndReEnableAutoFetch } = useProductsList({
    productsCacheKey: PRODUCTS_CACHE_KEY,
    hasMoreProductsCacheKey: HAS_MORE_PRODUCTS_CACHE_KEY,
  });
  const dispatch = useAppDispatch();
  const [queryParams, setQueryParams] = useState<Record<string, any>>(cleanSearchFilters(query));
  const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(query.menuOpen === 'true');
  const [breadcrumbsList, setBreadcrumbsList] = useState(jsonLdBreadcrumbs);
  const [isFilterDataLoading, setFiltersLoading] = useState<boolean>(false);
  const [productAvailableCategories, setProductAvailableCategories] = useState([]);
  const [isFilteringApplied, setFilteringApplied] = useState(false);

  // Search
  const searchKey: string = queryParams?.query || null;

  function getNewCacheKey(): string {
    return brand?.name + searchKey + JSON.stringify(queryParams) + `_multilang_${multilangSearchEnabled}_source_${sourceModule ?? ''}`;
  }

  const searchGuidRef = useRef<string | undefined>(undefined);
  const [searchGuid, setSearchGuid] = useState<string | undefined>(undefined);
  const searchEngineRef = useRef<string | undefined>(undefined);
  // Entry attribution (e.g. 'saved_search') — carried onto view_item_list / select_item and
  // preserved across filter redirects. Not a search filter, so it's read off the raw query.
  const sourceModule = query.source_module?.toString();

  // Load brand products
  const loadBrandProducts = async (page: number) => {
    // Default endpoint for fetching products
    const requestQueryParams: Record<string, any> = {
      ...queryParams,
      brandId: brand?.id,
      offset: PRODUCTS_PER_PAGE * page,
      limit: PRODUCTS_PER_PAGE,
      getFilterCategories: false,
      ...(sourceModule && { source: sourceModule }),
    };

    // Add mode parameter only when GrowthBook flag is enabled
    if (multilangSearchEnabled) {
      requestQueryParams.mode = 'multi';
    }

    // New search interaction at page 0 — generate fresh guid client-side.
    if (page === 0) {
      const newGuid = generateSearchGuid();

      searchGuidRef.current = newGuid;
      setSearchGuid(newGuid);
    }

    // Request next set of products
    const result = await productService.searchProducts(searchKey, requestQueryParams);

    if (page === 0) {
      searchEngineRef.current = result?.searchEngine;
      GTM.bumpedItemsViewed(result?.bumpedItems);
    }

    if (result?.list?.length) {
      GTM.viewItemList(result.list, SEARCH_LIST_ID.brand_browse, PRODUCTS_PER_PAGE * page, {
        source_module: sourceModule,
        search_engine: result?.searchEngine ?? searchEngineRef.current,
      });
    }

    if (page === 0 && searchGuidRef.current) {
      RecsysTracking.search({
        searchGuid: searchGuidRef.current,
        searchContext: 'brand_browse',
        searchEngine: result?.searchEngine,
        query: searchKey || null,
        filters: buildSearchTrackingFilters({ ...queryParams, brandId: brand?.id }),
        auto: false,
      });
    }

    return result;
  };

  async function loadFilterData() {
    try {
      setFiltersLoading(true);

      const searchParams: Record<string, any> = {
        brandId: brand?.id,
        offset: 0,
        limit: 1,
        getFilterCategories: true,
      };

      // Add mode parameter only when GrowthBook flag is enabled
      if (multilangSearchEnabled) {
        searchParams.mode = 'multi';
      }

      const { availableCategories } = await productService.searchProducts(searchKey, searchParams);

      setProductAvailableCategories(availableCategories || []);
    } catch (e) {
      captureException('Can not get search filter product data | ' + e?.message);
    } finally {
      setFiltersLoading(false);
    }
  }

  const initSeoBreadcrumbs = () => {
    const breadcrumbs = getJsonLdBreadcrumbsList(router);

    setBreadcrumbsList(breadcrumbs);
  };

  const onSearch = async (searchValue: string) => {
    // Temporarily disable infinite scroll from autofetching
    setHasMoreProducts(false);

    await redirectTo(
      {
        ...BRAND(toKebabCase(brand?.name)),
        query: cleanSearchFilters({
          query: searchValue,
        }),
      },
      true,
    );

    resetAndReEnableAutoFetch();
  };

  // Apply filters
  const onFilter = async (newFilterQueryParams: SearchFiltersQueryParams, additionalFilterInfo: Record<string, any>) => {
    // This detects the edge-case when selected brand itself is removed from query[brand-name].
    // Query params do not hold this info, so that's how we detect that brand name from the url has been removed.
    const wasBrandPageBrandDeselected = additionalFilterInfo?.wasBrandPageBrandDeselected;

    // If other brands than main one are present or the main one itself was deselected, navigate to usual search page.
    if (newFilterQueryParams[FILTER_MENU_ITEM.BRANDS] || wasBrandPageBrandDeselected) {
      if (brand?.id && !wasBrandPageBrandDeselected) {
        // Parse brand ids from query params.
        const brandsParsed = parseJSON(newFilterQueryParams[FILTER_MENU_ITEM.BRANDS]) ?? [];

        const isSameBrandSelectedFromOthers = brandsParsed?.length === 1 && brandsParsed?.[0] === brand.id;

        if (isSameBrandSelectedFromOthers) {
          // If we click on same brand that is already selected as main, we need to de-select it.
          delete newFilterQueryParams[FILTER_MENU_ITEM.BRANDS];
        } else {
          // Attach main brand to selections for global search page.
          brandsParsed.push(brand.id);
          newFilterQueryParams[FILTER_MENU_ITEM.BRANDS] = stringifyJSON(brandsParsed);
        }
      }

      const cleanQueryParams = cleanSearchFilters(newFilterQueryParams);

      // Remove search key from query params as it is attached to url param itself.
      delete (cleanQueryParams as Record<string, string>).query;

      return redirectTo(
        {
          query: { ...cleanQueryParams, ...(sourceModule && { source_module: sourceModule }) },
          ...SEARCH(searchKey ?? ''),
        },
        false,
      );
    }

    // Default brand page filtering.
    await redirectTo(
      {
        ...BRAND(toKebabCase(brand?.name)),
        query: { ...cleanSearchFilters(newFilterQueryParams), ...(sourceModule && { source_module: sourceModule }) },
      },
      true,
    );
  };

  // Reset filters
  const resetFilters = async () => {
    return redirectTo(
      {
        query: { ...(sourceModule && { source_module: sourceModule }) },
        ...SEARCH(searchKey ?? ''),
      },
      false,
    );
  };

  useEffect(() => {
    initSeoBreadcrumbs();
  }, []);

  // Update queryParams only when some of the value changes.
  useEffect(() => {
    const cleanQuery = cleanSearchFilters(query);

    if (!isEqual(cleanQuery, queryParams)) {
      setQueryParams(cleanQuery);
    }
  }, [query]);

  useEffect(() => {
    loadFilterData();
  }, [searchKey]);

  const headerIcon = (
    <HeaderIconLogo icon="back" showLogo={false}>
      <HeaderItem className="brand__info">
        {brand?.image && <img src={ImageUrlResizer.resize(brand?.image?.original, 150, false, 'png')} alt={brand?.name} />}

        {brand?.name}
      </HeaderItem>
    </HeaderIconLogo>
  );
  const headerSpacing = <HeaderItem className="flex-grow" />;

  const headerLogin = <HeaderLogin />;
  const headerSearch = (
    <Box width="100%" my={isMobileScreen ? 0 : 2}>
      <HeaderSearch
        placeholder="components.search-bar.search-products-placeholder"
        search={searchKey}
        originRoute={BRAND(toKebabCase(brand?.name))}
        onSearch={(searchValue) => {
          // On search change
          if (searchKey !== searchValue) {
            onSearch(searchValue);
          }
        }}
      />
    </Box>
  );
  const headerFilter = (
    <HeaderItem className={'ml-2-i'}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<img src="/icons/icn-search-filter.svg" className="settings-title-icon" alt="filter-search" />}
        className={classNames('search-page__filter-button', {
          'search-page__filter-button-active': isFilteringApplied,
        })}
        onClick={() => setFilterMenuOpen(true)}
      >
        {t('common.filter')}
      </Button>
    </HeaderItem>
  );

  const saveSearch = (className?: string, wrapperClassName?: string) =>
    brand?.id ? (
      <SaveSearchButton
        target={UserSavedSearchTargets.BRAND}
        className={className}
        wrapperClassName={wrapperClassName}
        queryParams={{
          ...queryParams,
          brands: stringifyJSON([brand.id]),
        }}
        searchKey={searchKey}
      />
    ) : null;

  // RENDER
  return (
    <>
      <NextSeo title={getSEOFriendlyTitle([brand?.name])} />

      {breadcrumbsList?.length > 0 && <BreadcrumbJsonLd itemListElements={breadcrumbsList} />}

      <Header
        mobileChildren={[
          [headerIcon, headerSpacing, headerLogin],
          [headerSearch, headerFilter],
          [headerSpacing, saveSearch('search-page__filter-button save-search-button')],
        ]}
      >
        {[
          [headerIcon, headerSpacing, headerLogin],
          [headerSearch],
          [headerSpacing, saveSearch('search-page__filter-button save-search-button'), headerFilter],
        ]}
      </Header>

      <div className="brand-page">
        {/* check if brand exists */}
        {!brand?.name ? (
          <NotFound target="category" />
        ) : (
          <FeaturesReady timeout={150}>
            <ProductsListInfiniteScroll
              searchKey={searchKey}
              searchGuid={searchGuid}
              batchSize={PRODUCTS_PER_PAGE}
              gtmLocation={GTMLocations.BRAND}
              cacheKey={CACHE_KEY}
              productsCacheKey={PRODUCTS_CACHE_KEY}
              hasMoreProductsCacheKey={HAS_MORE_PRODUCTS_CACHE_KEY}
              NotFoundComponent={() => <NotFound target="search" translationOpts={{ key: searchKey }} />}
              notFound={!!searchKey}
              loadProducts={loadBrandProducts}
              getNewCacheKey={getNewCacheKey}
              onItemClick={(index, product) => {
                dispatch(searchItemClick({ index }));
                GTM.selectItem(product, SEARCH_LIST_ID.brand_browse, index, {
                  source_module: sourceModule,
                  search_engine: searchEngineRef.current,
                });
              }}
            />
          </FeaturesReady>
        )}
      </div>

      {/* FILTERING MENU */}
      <SearchFilter
        open={filterMenuOpen}
        onOpen={setFilterMenuOpen}
        shopsExist={false}
        productsExist={true}
        activeSection="products"
        queryParams={queryParams}
        productCategories={productAvailableCategories}
        onFilter={onFilter}
        loading={isFilterDataLoading}
        onReset={resetFilters}
        onFilteringApplied={setFilteringApplied}
        gtmLocation={GTMLocations.BRAND}
      >
        {/* Save search button. */}
        {saveSearch(undefined, 'filter-menu-save-search-button mt-1')}

        <Typography component="div" variant="body2" className="save-search-info mt-2">
          {t('components.search.save-info')}
        </Typography>
      </SearchFilter>
    </>
  );
};

export function cleanSearchFilters(queryParams): SearchFiltersQueryParams {
  const ALLOWED_SEARCH_FILTERS = [
    ...Object.values(FILTER_MENU_ITEM),
    'query', // This is search key.
  ];

  return extractQueryParams(queryParams, ALLOWED_SEARCH_FILTERS);
}

export default BrandProducts;

export const getServerSideProps = async (ctx) => {
  const { query, req } = ctx;
  const brandName = query['brand-name'];
  const asPath = req?.url;
  let jsonLdBreadcrumbs = [];

  // Find selected brand
  const brands = await brandService.getBrands({}, { ctx });
  const currentBrand = (brands || [])?.find((brand) => toKebabCase(brand.name) === toKebabCase(brandName));

  if (asPath) {
    jsonLdBreadcrumbs = getJsonLdBreadcrumbsList({ asPath });
  }

  return {
    props: formatServerSideProps({
      brand: currentBrand,
      jsonLdBreadcrumbs,
    }),
  };
};
